import { invoke, isTauri } from "@tauri-apps/api/core";

import type { ConnectionCapability } from "@nlkb/api-contract/connections";
import type {
  OperationRequest,
  OperationResponse,
} from "@nlkb/api-contract/operations";

export interface DesktopEnrollmentInput {
  readonly session_token: string;
  readonly organization_id: string;
  readonly owner_principal_id: string;
  readonly name: string;
  readonly capabilities: readonly ConnectionCapability[];
}

export interface DesktopEnrollmentResponse {
  readonly connection_id: string;
  readonly vault_count: number;
  readonly service_state: "running" | "repair_required";
}

export interface DesktopMountStatus {
  readonly vault_id: string;
  readonly vault_type: string;
  readonly vault_name: string;
  readonly state: string;
  readonly pause_reason: string | null;
  readonly health_code: string;
  readonly applied_cursor: number;
  readonly queue_count: number;
  readonly oldest_queue_age_ms: number | null;
  readonly conflict_count: number;
  readonly incoming_count: number;
  readonly open_diagnostics: number;
  readonly last_scan_at: string | null;
  readonly last_sync_at: string | null;
}

export interface DesktopSyncStatus {
  readonly client_version: string;
  readonly protocol_version: number;
  readonly network_paused: boolean;
  readonly health_code: string;
  readonly last_heartbeat_at: string | null;
  readonly mounts: readonly DesktopMountStatus[];
}

export interface DesktopStatusResponse {
  readonly enrolled: boolean;
  readonly service_state: "running" | "stopped" | "not_installed" | "unknown";
  readonly status: DesktopSyncStatus | null;
}

export interface DesktopDiagnostics {
  readonly healthy: boolean;
  readonly checks: Readonly<Record<string, string>>;
  readonly mounts: Readonly<Record<string, string>>;
}

export interface DesktopUpdateStatus {
  readonly current_version: string;
  readonly available: boolean;
  readonly required: boolean;
  readonly version: string;
  readonly minimum_client_version: string;
  readonly suggested_client_version: string;
  readonly minimum_protocol_version: number;
  readonly maximum_protocol_version: number;
  readonly release_notes: string;
  readonly published_at: string;
  readonly artifact_operating_system: string;
  readonly artifact_architecture: string;
  readonly artifact_sha256: string;
  readonly artifact_byte_length: number;
}

export interface ServiceControlResponse {
  readonly action: "install" | "repair" | "start" | "stop" | "status";
  readonly state: string;
}

export interface UpdateInstallResponse {
  readonly state: "installed" | "scheduled" | "current";
  readonly version: string;
}

export interface ViewerUpdateStatus {
  readonly available: boolean;
  readonly current_version: string;
  readonly version: string;
  readonly release_notes: string;
}

export interface ViewerUpdateInstallResponse {
  readonly state: "installed";
  readonly version: string;
}

export class DesktopBridgeError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "DesktopBridgeError";
  }
}

export function isDesktopRuntime(): boolean {
  return isTauri();
}

export function isDesktopShellLocation(location: {
  readonly hostname: string;
  readonly protocol: string;
}): boolean {
  return (
    location.hostname === "tauri.localhost" ||
    location.hostname === "asset.localhost" ||
    location.protocol === "tauri:"
  );
}

export function isDesktopShell(): boolean {
  if (typeof window === "undefined") return false;
  return isDesktopShellLocation({
    hostname: window.location.hostname,
    protocol: window.location.protocol,
  });
}

async function invokeDesktop<T>(
  command: string,
  arguments_: Record<string, unknown>,
): Promise<T> {
  try {
    return await invoke<T>(command, arguments_);
  } catch (error) {
    if (
      typeof error === "object" &&
      error !== null &&
      "code" in error &&
      typeof error.code === "string"
    ) {
      throw new DesktopBridgeError(error.code);
    }
    throw new DesktopBridgeError("desktop_bridge_failed");
  }
}

export function enrollDesktop(
  input: DesktopEnrollmentInput,
): Promise<DesktopEnrollmentResponse> {
  return invokeDesktop("enroll_desktop", { input });
}

export function desktopStatus(): Promise<DesktopStatusResponse> {
  return invokeDesktop("desktop_status", {});
}

export function desktopDiagnostics(): Promise<DesktopDiagnostics> {
  return invokeDesktop("desktop_diagnostics", {});
}

export function setDesktopSyncPaused(
  paused: boolean,
): Promise<DesktopSyncStatus> {
  return invokeDesktop("set_sync_paused", { paused });
}

export function openDesktopVaultFolder(vaultId: string): Promise<void> {
  return invokeDesktop("open_vault_folder", { vaultId });
}

export function submitDesktopOperation(
  vaultId: string,
  operation: OperationRequest,
): Promise<OperationResponse> {
  return invokeDesktop("submit_desktop_operation", { vaultId, operation });
}

export function controlDesktopService(
  action: ServiceControlResponse["action"],
): Promise<ServiceControlResponse> {
  return invokeDesktop("service_control", { action });
}

export function checkDesktopUpdate(): Promise<DesktopUpdateStatus> {
  return invokeDesktop("check_desktop_update", {});
}

export function installDesktopUpdate(): Promise<UpdateInstallResponse> {
  return invokeDesktop("install_desktop_update", {});
}

export function checkViewerUpdate(): Promise<ViewerUpdateStatus> {
  return invokeDesktop("check_viewer_update", {});
}

export function installViewerUpdate(): Promise<ViewerUpdateInstallResponse> {
  return invokeDesktop("install_viewer_update", {});
}
